import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Flex,
    Stack
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { auditsService } from "services/auditsService";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { AuditFilter } from "./AuditFilter";

function AuditList() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useState({orderBy: "data_hora", orderDirection: "desc"});
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();

    const fetchData = async (params = {}) => {
        if (response) setResponse(undefined);

        const page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        const newParams = { ...searchParams, ...params,  page: page};
        setSearchParams(newParams);
    
        try {
            const data = await auditsService.list(newParams);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(newParams));
            showToast({ title: t(err), status: "error" });
        }
    };
    
    const handleFilter = (newFilters) => {
        fetchData({
            filters: newFilters,
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const columns = useMemo(() => ([
        {
            Header: t("settings.audit.list.table.header.date"),
            accessor: "data_hora",
        },
        {
            Header: t("settings.audit.list.table.header.ip"),
            accessor: "ip",
        },
        {
            Header: t("settings.audit.list.table.header.user"),
            accessor: "usuario",
        },
        {
            Header: t("settings.audit.list.table.header.url"),
            accessor: "url",
        },
        {
            Header: t("settings.audit.list.table.header.method"),
            accessor: "metodo",
        },
        {
            Header: t("settings.audit.list.table.header.request"),
            accessor: "request",
        },
        {
            Header: t("settings.audit.list.table.header.consult"),
            accessor: "query",
        },
        {
            Header: t("settings.audit.list.table.header.parameters"),
            accessor: "binds",
        },
    ]), []);

    return (
        <Flex direction="column" pt={{ base: "150px", sm: "125px", lg: "75px" }}>
            <Flex
                direction={{ sm: 'column', md: 'row' }}
                justify='flex-end'
                align='center'
                alignSelf={{ sm: 'flex-start', lg: undefined }}
                px="30px"
                w='100%'
                mt='24px'
                mb='24px'
            >
                <Stack
                    direction='row'
                    spacing='10px'
                    alignSelf={{ sm: 'flex-start', lg: 'auto' }}
                >
                    <AuditFilter onFilter={handleFilter} />
                </Stack>
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("settings.audit.title")} subtitle={t("settings.audit.description")} />
                <CardBody>
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "data_hora", ...searchParams })}
                            onChange={fetchData}
                            disableSearch={true}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
}

export default AuditList;
